import { ContentLink, LoadStatus } from '@servicetitan/cp-ui';
import { Banner, BodyText, Button, Form } from '@servicetitan/design-system';
import { provide, useDependencies } from '@servicetitan/react-ioc';
import { ContactMethod } from 'api';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import {
    ChangeEvent,
    FC,
    FormEvent,
    Fragment,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthMfaVerificationStore } from '../../stores/mfa-verification.store';
import { useAuthHistory, useAuthMfaQueryParams } from '../../utils/use-auth-history';
import { AuthLayout } from '../layout/auth.layout';
import { AuthHeadline } from '../layout/components';
import * as Styles from './mfa-verification.module.less';

const StepIndicator: FC<{ label: string; done?: boolean; active?: boolean }> = ({
    active,
    done,
    label,
}) => (
    <div
        className={classNames(Styles.stepperStep, {
            [Styles.stepperStepDone]: done,
            [Styles.stepperStepActive]: active,
        })}
    >
        <div
            className={classNames(Styles.stepperDot, {
                [Styles.stepperDotDone]: done,
                [Styles.stepperDotActive]: active,
            })}
        >
            {done ? '\u2713' : active ? '2' : ''}
        </div>
        <span>{label}</span>
    </div>
);

const AuthMfaVerificationProvider = provide({
    singletons: [AuthMfaVerificationStore],
})(({ children }) => children);

export const AuthMfaVerificationComponent = observer(() => {
    const [
        {
            canSubmit,
            codeLength,
            codeLifetimeMinutes,
            form,
            init,
            mfaContactMethod,
            resend,
            resendCooldownSeconds,
            resendStatus,
            submit,
            submitStatus,
        },
    ] = useDependencies(AuthMfaVerificationStore);

    const location = useLocation();
    const locationState = location.state as {
        mfaSessionToken?: string;
        mfaCodeLength?: number;
        mfaCodeLifetimeMinutes?: number;
        mfaResendCooldownSeconds?: number;
        mfaContactMethod?: ContactMethod;
    };
    const mfaSessionToken = locationState?.mfaSessionToken;
    const queryParams = useAuthMfaQueryParams();
    const history = useAuthHistory();
    const navigate = useNavigate();

    const { code } = form.$;
    const inputRef = useRef<HTMLInputElement>(null);
    const [isFocused, setIsFocused] = useState(false);
    const [resendCountdown, setResendCountdown] = useState(0);

    useEffect(() => {
        if (!mfaSessionToken) {
            navigate(history.href('login'), { replace: true });
            return;
        }
        init(
            mfaSessionToken,
            queryParams.keepSigned,
            locationState?.mfaCodeLength,
            locationState?.mfaCodeLifetimeMinutes,
            locationState?.mfaResendCooldownSeconds,
            locationState?.mfaContactMethod
        );
    }, [
        mfaSessionToken,
        queryParams.keepSigned,
        init,
        navigate,
        history,
        locationState?.mfaCodeLength,
        locationState?.mfaCodeLifetimeMinutes,
        locationState?.mfaResendCooldownSeconds,
        locationState?.mfaContactMethod,
    ]);

    useEffect(() => {
        if (submitStatus === LoadStatus.Ok) {
            navigate('/');
        }
    }, [submitStatus, navigate]);

    useEffect(() => {
        if (resendCountdown <= 0) {
            return;
        }
        const timer = setInterval(() => {
            setResendCountdown(prev => (prev <= 1 ? 0 : prev - 1));
        }, 1000);
        return () => clearInterval(timer);
    }, [resendCountdown]);

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault();
        submit();
    };

    const handleResend = useCallback(() => {
        resend();
        setResendCountdown(resendCooldownSeconds);
    }, [resend, resendCooldownSeconds]);

    const handleOtpChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            const filtered = e.target.value
                .replace(/[^a-zA-Z0-9]/g, '')
                .toUpperCase()
                .slice(0, codeLength);
            code.onChange(filtered);
        },
        [code, codeLength]
    );

    const focusInput = () => inputRef.current?.focus();

    const value = code.value;
    const hasError = submitStatus === LoadStatus.Error;
    const activeIdx = Math.min(value.length, codeLength - 1);
    const isSms = mfaContactMethod === ContactMethod.PhoneNumber;

    return (
        <AuthLayout>
            {/* Step indicator */}
            <div className={Styles.stepper}>
                <StepIndicator label="Sign In" done />
                <div className={classNames(Styles.stepperLine, Styles.stepperLineDone)} />
                <StepIndicator label="Verify" active />
            </div>

            {/* Back link */}
            <ContentLink href={history.href('login')} className={Styles.backLink}>
                &larr; Back to sign in
            </ContentLink>

            <AuthHeadline title={isSms ? 'Check Your Phone' : 'Check Your Email'} />

            <BodyText subdued className="ta-center m-b-half">
                {isSms
                    ? 'We sent a verification code as a text message to your phone number.'
                    : 'We sent a verification code to your email address.'}
            </BodyText>
            <BodyText subdued className="ta-center m-b-3">
                This code expires in <strong>{codeLifetimeMinutes} minutes</strong>.
            </BodyText>

            {/* Banners */}
            {hasError && (
                <Banner status="warning" icon title="Incorrect code" className="m-b-2">
                    The code you entered doesn&apos;t match. Please try again or request a new code.
                </Banner>
            )}

            {resendStatus === LoadStatus.Ok && (
                <Banner
                    status="success"
                    icon
                    title={isSms ? 'New code sent to your phone' : 'New code sent to your email'}
                    className="m-b-2"
                />
            )}

            {resendStatus === LoadStatus.Error && (
                <Banner
                    status="warning"
                    icon
                    title="Please wait before requesting another code"
                    className="m-b-2"
                />
            )}

            <Form onSubmit={handleSubmit}>
                {/* OTP visual input */}
                <div className={Styles.otpWrap} onClick={focusInput}>
                    <div className={Styles.otpSlots} aria-hidden="true">
                        {Array.from({ length: codeLength }, (_, i) => {
                            const ch = value[i] || '';
                            const isActive = isFocused && i === activeIdx;
                            const isFilled = !!ch;

                            return (
                                <div
                                    key={i}
                                    className={classNames(Styles.otpSlot, {
                                        [Styles.otpSlotActive]: isActive && !hasError,
                                        [Styles.otpSlotFilled]: isFilled && !hasError,
                                        [Styles.otpSlotError]: hasError,
                                    })}
                                >
                                    {ch ||
                                        (isActive && !hasError ? (
                                            <div className={Styles.otpCaret} />
                                        ) : null)}
                                </div>
                            );
                        })}
                    </div>
                    <input
                        ref={inputRef}
                        className={Styles.otpHiddenInput}
                        type="text"
                        inputMode="text"
                        autoComplete="one-time-code"
                        maxLength={codeLength}
                        aria-label="Verification code"
                        value={value}
                        onChange={handleOtpChange}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                        autoFocus
                    />
                </div>

                <Button
                    width="full"
                    primary
                    onClick={submit}
                    disabled={!canSubmit}
                    loading={submitStatus === LoadStatus.Loading}
                    className="m-t-2"
                >
                    Verify
                </Button>

                <BodyText className="ta-center m-t-3">
                    {resendCountdown > 0 ? (
                        <Fragment>
                            You can request a new code in <strong>{resendCountdown} seconds</strong>
                            .
                        </Fragment>
                    ) : (
                        <Fragment>
                            {isSms
                                ? "Didn't get a text message? "
                                : "Didn't get an email? Check your spam folder or: "}
                            <Button
                                fill="subtle"
                                primary
                                small
                                type="button"
                                onClick={handleResend}
                                disabled={resendStatus === LoadStatus.Loading}
                            >
                                {resendStatus === LoadStatus.Loading ? 'Sending...' : 'Resend code'}
                            </Button>
                        </Fragment>
                    )}
                </BodyText>
            </Form>
        </AuthLayout>
    );
});

export const AuthMfaVerification = () => (
    <AuthMfaVerificationProvider>
        <AuthMfaVerificationComponent />
    </AuthMfaVerificationProvider>
);
